<template>
  <div v-if="diagram">
    <div class="vz-order-diagram-title">
      <span class="text-h1 color-dark flex-grow-1">График перевозки</span>
      <span class="text-h1 color-dark mr-10">{{ countDeliveryDate }}</span>
      <vz-button :icon-name="toggleIcon" rounded class="ml-auto pdf-order-section-hide" @click="onClick" />
    </div>
    <div class="vz-order-diagram-details" v-if="isVisible">
      <div v-if="firstItem" class="vz-order-diagram-details-item mt-20 pb-10">
        <span class="graph first" :class="{'passed':firstItem.state}" />
        <div class="text-body color-dark">
          {{ cargoPickupTitle }}
        </div>
        <div v-if="firstItem" class="text-body color-light">
          <span>{{ firstGroupDate }}</span>
          <span
            v-for="(group, index) of diagram"
            :key="index"
            class="vz-order-diagram-details-grouptime"
            :class="{active: selectedGroupTime === group.groupTime}"
            @click="onGroupSelect(group.groupTime)"
          > <span v-if="group.groupTime"> до {{ group.groupTime }} </span>
          </span>
        </div>
      </div>

      <div v-for="(item, index) of intermediateItems" :key="index" class="vz-order-diagram-details-item pb-10">
        <span class="graph" :class="{'passed':item.state}" />
        <div class="text-body color-dark">
          {{ item.locationName }}
        </div>
        <div class="text-body color-light">
          {{ formatDate(item.date) }}
        </div>
      </div>

      <div v-if="lastItem" class="vz-order-diagram-details-item pb-10">
        <span class="graph" :class="{'passed':lastItem.state}" />
        <div class="text-body color-dark">
          {{ cargoDeliveryTitle }}
        </div>
        <div class="text-body color-light">
          <span>{{ lastGroupDate }}</span>
          <span
            v-for="(group, index) of lastGroupTime"
            :key="index"
            class=""
          > {{ index === 0 ? ' с' : ' до' }} {{ group }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {OrderDiagramGroup, OrderForm, OrderView} from '~/types/types';
import {formatDate} from '~/common/date';
import {declOfNum, deepCopyObject} from '~/common/functions';
import { useDayjs } from '#dayjs'
import {PropType} from 'vue';

const emit = defineEmits(['visible-toggle'])

const props = defineProps({
  visible: {
    type: Boolean,
    default: true
  },
  order: {
    type: Object as PropType<OrderView|OrderForm>,
    default: () => ({ directions: { dispatch: { calendar: {} }, destination: { calendar: {} } }  })
  },
})

const dayjs = useDayjs()
const isVisible = ref(true)
const selectedGroup = ref('')

const diagram = computed(() => {
  return props.order?.shippingDiagram || []
})

const cargoPickupTitle = computed(() => {
  return props.order.directions.dispatch.direction === 'terminal' ? 'Самостоятельная доставка' : 'Забор с адреса'
})

const cargoDeliveryTitle = computed(() => {
  switch (props.order.directions.destination.direction) {
    case 'terminal':
      return 'Самовывоз с терминала'
    case 'address':
      return 'Доставка до адреса'
    case 'pickupPoint':
      return 'Доставка в пункт выдачи'
  }
})

const toggleIcon = computed(() => {
  return isVisible.value ? 'chevron-up' : 'chevron-down'
})

const firstGroupDate = computed(() => {
  return formatDate(props.order.directions.dispatch.calendar.date)
})

const lastGroupDate = computed(() => {
  return formatDate(props.order.directions.destination.calendar.date)
})

const group = computed((): OrderDiagramGroup => {
  const group = diagram.value.find(d => d.groupTime === selectedGroup.value)
  if (group) {
    return group
  }
  selectedGroup.value = diagram.value[0]?.groupTime
  return diagram.value[0]
})

const firstItem = computed(() => {
  return group.value?.items[0] || null
})

const lastItem = computed(() => {
  return group.value?.items[group.value?.items.length - 1] || null
})

const groupTime = computed(() => {
  return group.value?.groupTime || ''
})

const lastGroupTime = computed(() => {
  if (props.order.directions.destination.direction === 'address') {
    return [
      props.order.directions.destination.calendar.from,
      props.order.directions.destination.calendar.to
    ]
  }
  return [props.order.directions.destination.calendar.terminalTime]
})

const countDeliveryDate = computed(() => {
  const declDays = ['день', 'дня', 'дней']
  const firstDate = props.order.directions.dispatch.calendar.date
  const lastDate = props.order.directions.destination.calendar.date
  const date1 = dayjs(firstDate)
  const date2 = dayjs(lastDate)

  if (!lastDate) {
    return ''
  }

  const days = date2.diff(date1, 'days')
  return `${days} ${declOfNum(days, declDays)}`
})

const selectedGroupTime = computed(() => {
  if (!selectedGroup.value) {
    return group.value?.groupTime || ''
  }
  return selectedGroup.value
})

const intermediateItems = computed(() => {
  if (group.value) {
    const items = deepCopyObject(group.value?.items)
    if (items) {
      items.pop()
      items.shift()
      return items
    }
  }
  return []
})

function onGroupSelect(group: string): void {
  selectedGroup.value = group || ''
}

function onClick() {
  isVisible.value = !isVisible.value
  emit('visible-toggle', isVisible.value)
}

watch(() => props.visible, () => isVisible.value = props.visible, { immediate: true })
</script>
